import { DE_VERBS, dePresent } from './german';
import { type LanguageTenses, type Tense } from './tenses';

// ─────────────────────────────────────────────────────────────────────────────
// German: every tense beyond the Präsens (its own topic). WEAK verbs only —
// strong verbs (schwimmen, trinken, finden, heißen…) change their vowel in the
// past and are left out rather than given wrong forms.
//
//   • -te past with an e after d / t / consonant+m,n: arbeitete, öffnete
//   • ge-…-t participle (gearbeitet); reisen takes sein: ist gereist
// ─────────────────────────────────────────────────────────────────────────────

const STRONG = new Set(['schwimmen', 'trinken', 'finden', 'heißen']);
const WEAK = Object.fromEntries(Object.entries(DE_VERBS).filter(([v]) => !STRONG.has(v)));
const SEIN_VERBS = new Set(['reisen']);

const stem = (v: string) => v.slice(0, -2);
const needsE = (s: string) => /[dt]$/.test(s) || /[^aeiouäöülrhmn][mn]$/.test(s);
const pastStem = (v: string) => stem(v) + (needsE(stem(v)) ? 'ete' : 'te');
const participle = (v: string) => 'ge' + stem(v) + (needsE(stem(v)) ? 'et' : 't');

const PAST_END = ['', 'st', '', 'n', 't', 'n'];
const HABEN = ['habe', 'hast', 'hat', 'haben', 'habt', 'haben'];
const SEIN = ['bin', 'bist', 'ist', 'sind', 'seid', 'sind'];
const HATTE = ['hatte', 'hattest', 'hatte', 'hatten', 'hattet', 'hatten'];
const WAR = ['war', 'warst', 'war', 'waren', 'wart', 'waren'];
const WERDEN = ['werde', 'wirst', 'wird', 'werden', 'werdet', 'werden'];
const WUERDE = ['würde', 'würdest', 'würde', 'würden', 'würdet', 'würden'];
const HAETTE = ['hätte', 'hättest', 'hätte', 'hätten', 'hättet', 'hätten'];
const WAERE = [['wäre'], ['wärst', 'wärest'], ['wäre'], ['wären'], ['wärt', 'wäret'], ['wären']];
const KONJ1 = ['e', 'est', 'e', 'en', 'et', 'en'];

const perfektAux = (v: string, i: number) => (SEIN_VERBS.has(v) ? SEIN[i] : HABEN[i]);

function imperative(v: string, i: number): string[] | null {
  const s = stem(v);
  if (i === 1) return needsE(s) ? [s + 'e'] : [s, s + 'e'];     // arbeite! · mach! / mache!
  if (i === 4) return [dePresent(v, 4)];                          // macht!
  if (i === 5) return [`${v} Sie`];                               // machen Sie!
  return null;
}

const TENSES: Tense[] = [
  { id: 'praeteritum', title: 'Präteritum (simple past)', blurb: 'ich machte, du arbeitetest…', verbs: WEAK,
    forms: (v, i) => [pastStem(v) + PAST_END[i]] },
  { id: 'perfekt', title: 'Perfekt', blurb: 'ich habe gemacht, er ist gereist…', verbs: WEAK,
    forms: (v, i) => [`${perfektAux(v, i)} ${participle(v)}`] },
  { id: 'plusquamperfekt', title: 'Plusquamperfekt', blurb: 'ich hatte gemacht, wir waren gereist…', verbs: WEAK,
    forms: (v, i) => [`${SEIN_VERBS.has(v) ? WAR[i] : HATTE[i]} ${participle(v)}`] },
  { id: 'futur1', title: 'Futur I', blurb: 'ich werde machen…', verbs: { ...DE_VERBS },
    forms: (v, i) => [`${WERDEN[i]} ${v}`] },
  { id: 'futur2', title: 'Futur II', blurb: 'ich werde gemacht haben…', verbs: WEAK,
    forms: (v, i) => [`${WERDEN[i]} ${participle(v)} ${SEIN_VERBS.has(v) ? 'sein' : 'haben'}`] },
  { id: 'konjunktiv2', title: 'Konjunktiv II (würde)', blurb: 'ich würde machen…', verbs: { ...DE_VERBS },
    forms: (v, i) => [`${WUERDE[i]} ${v}`] },
  { id: 'konjunktiv2_past', title: 'Konjunktiv II (past)', blurb: 'ich hätte gemacht, wir wären gereist…', verbs: WEAK,
    forms: (v, i) => (SEIN_VERBS.has(v) ? WAERE[i].map(w => `${w} ${participle(v)}`) : [`${HAETTE[i]} ${participle(v)}`]) },
  { id: 'konjunktiv1', title: 'Konjunktiv I (reported speech)', blurb: 'er mache, sie arbeite…', verbs: WEAK,
    forms: (v, i) => [stem(v) + (stem(v).endsWith('e') ? KONJ1[i].replace(/^e/, '') : KONJ1[i])] },
  { id: 'imperativ', title: 'Imperativ', blurb: 'mach!, arbeitet!, warten Sie!', verbs: WEAK,
    people: [1, 4, 5], forms: imperative, who: i => ['', '(du)', '', '', '(ihr)', '(Sie)'][i] },
];

export const DE_TENSES: LanguageTenses = {
  lang: 'de',
  people: [['ich'], ['du'], ['er', 'es', 'sie (she)'], ['wir'], ['ihr'], ['sie (they)', 'Sie']],
  tenses: TENSES,
};
